// =============================================================================
// BP-08-05: Vendor Portal Frontend - ReviewResponseForm Component
// =============================================================================

import React, { useState } from 'react';
import { Send, X, Loader2 } from 'lucide-react';

interface ReviewResponseFormProps {
  initialValue?: string;
  onSubmit: (response: string) => Promise<void>;
  onCancel: () => void;
}

export function ReviewResponseForm({
  initialValue = '',
  onSubmit,
  onCancel,
}: ReviewResponseFormProps) {
  const [response, setResponse] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (response.trim().length < 20) {
      setError('Response must be at least 20 characters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(response.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-cream rounded-lg p-4">
      <div className="mb-3">
        <label htmlFor="response" className="block text-sm font-medium text-navy mb-2">
          Your Response
        </label>
        <textarea
          id="response"
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          rows={4}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors resize-none"
          placeholder="Write a professional and helpful response to this review..."
          disabled={isSubmitting}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-warm-gray">
            {response.length} characters (minimum 20)
          </p>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-warm-gray max-w-md">
          Tip: Acknowledge the feedback, address specific concerns, and thank the customer for their review.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-navy hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || response.trim().length < 20}
            className="flex items-center gap-2 px-4 py-2 bg-terracotta text-white text-sm font-medium rounded-lg hover:bg-terracotta-light transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Response
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

export default ReviewResponseForm;
